import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { Minus, Plus, X, Loader2, Lock } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { formatMoney } from '../lib/format';

const CartDrawer = () => {
  const {
    items,
    isOpen,
    setIsOpen,
    updateQty,
    removeItem,
    subtotal,
    count,
    currency,
    loading,
  } = useCart();
  const navigate = useNavigate();

  const goToCheckout = () => {
    setIsOpen(false);
    navigate('/checkout');
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        className="bg-[#F5EDDD] border-l border-[#C9B596] w-full sm:max-w-md flex flex-col p-0"
      >
        <SheetHeader className="px-6 py-5 border-b border-[#C9B596]">
          <SheetTitle className="text-[#33200F] flex items-center justify-between font-normal">
            <span
              className="text-2xl"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Your Cart <span className="text-[#A07A4F]">({count})</span>
            </span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-20">
              <p
                className="text-[#A07A4F] mb-6 italic"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20 }}
              >
                Your cart is quiet.
              </p>
              <Button
                onClick={() => setIsOpen(false)}
                asChild
                className="bg-[#33200F] text-[#F5EDDD] hover:bg-[#1E140B] rounded-none h-12 px-8 text-[12px] tracking-[0.22em] uppercase"
              >
                <Link to="/collections">Shop the collection</Link>
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-[#C9B596]">
              {items.map((item) => (
                <li key={item.key} className="py-5 flex gap-4">
                  <div className="w-20 h-24 bg-[#EDE3CF] overflow-hidden shrink-0">
                    {item.image && (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <Link
                          to={`/products/${item.id}`}
                          onClick={() => setIsOpen(false)}
                          className="text-[#33200F] text-[16px] leading-snug hover:text-[#5B3A24]"
                          style={{ fontFamily: "'Cormorant Garamond', serif" }}
                        >
                          {item.name}
                        </Link>
                        <p className="text-[12px] text-[#A07A4F] mt-1">Size: {item.size}</p>
                      </div>
                      <button
                        onClick={() => removeItem(item.lineId)}
                        className="text-[#A07A4F] hover:text-[#33200F]"
                        disabled={loading}
                      >
                        <X className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center border border-[#C9B596]">
                        <button
                          onClick={() => updateQty(item.lineId, item.qty - 1)}
                          className="px-2 py-1.5 text-[#33200F] hover:bg-[#EDE3CF] disabled:opacity-50"
                          disabled={loading}
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="px-3 text-[13px] text-[#33200F] min-w-[28px] text-center">
                          {item.qty}
                        </span>
                        <button
                          onClick={() => updateQty(item.lineId, item.qty + 1)}
                          className="px-2 py-1.5 text-[#33200F] hover:bg-[#EDE3CF] disabled:opacity-50"
                          disabled={loading}
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <p className="text-[#33200F] text-[14px]">
                        {formatMoney(item.price * item.qty, item.currency || currency)}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#C9B596] px-6 py-5 bg-[#F5EDDD]">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[12px] tracking-[0.22em] uppercase text-[#A07A4F]">Subtotal</span>
              <span
                className="text-[#33200F] text-2xl"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {formatMoney(subtotal, currency)} {currency}
              </span>
            </div>
            <p className="text-[12px] text-[#A07A4F] mb-4">
              Shipping calculated at checkout. Free Australia-wide.
            </p>
            <Button
              onClick={goToCheckout}
              disabled={loading || !items.length}
              data-testid="cart-checkout-btn"
              className="w-full bg-[#33200F] text-[#F5EDDD] hover:bg-[#1E140B] rounded-none h-12 text-[12px] tracking-[0.22em] uppercase disabled:opacity-60"
            >
              <span className="inline-flex items-center gap-2">
                <Lock className="h-3.5 w-3.5" strokeWidth={1.5} /> Checkout
              </span>
            </Button>
            <button
              onClick={() => setIsOpen(false)}
              className="w-full mt-3 text-[13px] text-[#A07A4F] underline underline-offset-4 hover:text-[#33200F]"
            >
              Continue shopping
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
